/**
 * AI display mode — while the data-assistant panel is open, the AI owns the
 * map entirely: every operational layer is hidden unless the agent has put a
 * filter on it (`show_only_features`), and filtered layers render ONLY the
 * matching features, in the app's own symbology.
 *
 * This is a render-time override: the user's legend and manual filter state
 * are never mutated, so closing the panel (aiMode off + clearAll) restores
 * the manual app exactly as the user left it. Keyed by MapLibre layer id.
 */

import type { ExpressionSpecification } from 'maplibre-gl';

type AgentLayerFilter = {
	// null = show the whole layer (no filter); otherwise only matching features.
	expression: ExpressionSpecification | null;
	// Short human-readable description, surfaced in the chat panel chip.
	label: string | null;
};

function createAgentFilterStore() {
	let aiMode = $state(false);
	let layerFilters = $state<Record<string, AgentLayerFilter>>({});

	const active = $derived(Object.keys(layerFilters).length > 0);
	// Deduped: one WFS layer can drive several map layers (e.g. the accidents
	// heatmap + circles), each carrying the same label.
	const labels = $derived([
		...new Set(
			Object.values(layerFilters)
				.map((entry) => entry.label)
				.filter((label): label is string => Boolean(label))
		)
	]);

	return {
		get aiMode() {
			return aiMode;
		},
		get layerFilters() {
			return layerFilters;
		},
		get active() {
			return active;
		},
		get labels() {
			return labels;
		},

		/** Enter/exit AI display mode. Exiting clears all agent filters. */
		setAiMode(enabled: boolean) {
			aiMode = enabled;
			if (!enabled) {
				layerFilters = {};
			}
		},

		setFilter(layerId: string, expression: ExpressionSpecification | null, label: string | null) {
			layerFilters = { ...layerFilters, [layerId]: { expression, label } };
		},

		removeFilter(layerId: string) {
			const { [layerId]: removed, ...rest } = layerFilters;
			void removed;
			layerFilters = rest;
		},

		clearAll() {
			layerFilters = {};
		}
	};
}

export const agentFilterStore = createAgentFilterStore();
